import React, {useEffect, useMemo, useState} from "react";
import "./Sanitation.css";
import Header from "../../../components/common/Header/Header";
import {useDispatch, useSelector} from "react-redux";
import RootState from "../../../redux/states/root.state";
import StepperRow, { StepperRows } from "../../../components/common/Stepper/StepperRow";
import { SpaConstLabels } from "../../../mqttData/consts/spaConstsLabels.enums";
import { SpaLiveLabels } from "../../../mqttData/live/spaLiveLabels.enums";
import { SpaSettingsLabels } from "../../../mqttData/settings/spaSettingsLabels";
import useSpaCommand from "../../../hooks/useSpaCommand";
import { useParams } from "react-router-dom";
import { AppDispatch } from "../../../redux/store";
import { fetchSpa } from "../../../redux/actions/spa.actions";
import { inputDebounce, settingsDebounce } from "../debounceTimes";

const Sanitation = () => {
    const dispatch: AppDispatch = useDispatch();
    const {id} = useParams();
    const spa = useSelector((state: RootState) => state.spa.data);
    const isConnected = useSelector((state: RootState) => 
        state.spa.currentSpaId === id && state.spa.isCurrentSpaConnected
    );
    const isConnecting = useSelector((state: RootState) => 
        state.spa.currentSpaId === id && state.spa.isConnecting
    );
    const settingsData = useSelector((state: RootState) => state.settings.data);
    const liveData = useSelector((state: RootState) => state.live.data);
    const constData = useSelector((state: RootState) => state.const.data);

    
    // Fetch SPA From the DB
    useEffect(() => {
        if (id != null) {
            dispatch(fetchSpa(id));
        }
    }, [dispatch, id]);

    // Live connection + command publisher (V2 or V1 spa):
    const command = useSpaCommand(id ?? "");

    let valueMapper: { [p: number]: string };

    valueMapper = {
      0:'OFF',
      1:'P1 LOW',
      2:'P1 HIGH',
      3:'YESS',
    }

    let subtitleMapper: { [p: number]: string };

    subtitleMapper = {
      0:'Do not run Ozone',
      1:'Ozone will run in conjunction with Pump 1 low speed',
      2:'Ozone will run in conjunction with Pump 1 high speed',
      3:'Ozone will run in conjunction with Yess',
    }

    /* Filters functionality */
    const [ozoneCycle, setOzoneCycle] = useState(0);
    const [ozoneHours, setOzoneHours] = useState(0);
    const [onzenHours, setOnzenHours] = useState(0);

    // Debounce logic states
    const [lastUserOzCy, setLastUserOzCy] = useState<number | null>(null);
    const [lastUpdateOzCy, setLastUpdateOzCy] = useState<number>(0);
    const [lastUserOzHr, setLastUserOzHr] = useState<number | null>(null);
    const [lastUpdateOzHr, setLastUpdateOzHr] = useState<number>(0);
    const [lastUserOnHr, setLastUserOnHr] = useState<number | null>(null);
    const [lastUpdateOnHr, setLastUpdateOnHr] = useState<number>(0);

    useEffect(() => {
        if(!settingsData) return;

        if (lastUserOzCy === null && settingsData.OzCy !== undefined)
            setOzoneCycle(settingsData.OzCy);

        if (lastUserOzHr === null && settingsData.OzHr !== undefined)
            setOzoneHours(settingsData.OzHr);

        if (lastUserOnHr === null && settingsData.OnHr !== undefined)
            setOnzenHours(settingsData.OnHr);
    }, [settingsData, lastUserOzCy, lastUserOzHr, lastUserOnHr]);

    // Sync local state with backend for ozoneCycle
    useEffect(() => {
        if (settingsData.OzCy !== undefined) {
            const now = Date.now();
            const backendOzCy = settingsData.OzCy as number;
            // Only update if user is NOT actively changing, debounce window expired, and value is different
            if (
                lastUserOzCy === null &&
                now - lastUpdateOzCy > settingsDebounce &&
                ozoneCycle !== backendOzCy
            ) {
                setOzoneCycle(backendOzCy);
            }
            // If backend confirms the last user value, clear lastUserOzCy (but don't update UI)
            if (lastUserOzCy !== null && backendOzCy === lastUserOzCy) {
                setLastUserOzCy(null);
            }
        }
    }, [settingsData.OzCy]);

    // Debounced effect to send ozoneCycle
    useEffect(() => {
        if (lastUserOzCy !== null && ozoneCycle === lastUserOzCy) {
            const timer = setTimeout(() => {
                command.updateSettings({ setOzCy: lastUserOzCy });
            }, inputDebounce);
            return () => clearTimeout(timer);
        }
    }, [lastUserOzCy, ozoneCycle]);

    // Sync local state with backend for ozoneHours
    useEffect(() => {
        if (settingsData.OzHr !== undefined) {
            const now = Date.now();
            const backendOzHr = settingsData.OzHr as number;
            if (
                lastUserOzHr === null &&
                now - lastUpdateOzHr > settingsDebounce &&
                ozoneHours !== backendOzHr
            ) {
                setOzoneHours(backendOzHr);
            }
            if (lastUserOzHr !== null && backendOzHr === lastUserOzHr) {
                setLastUserOzHr(null);
            }
        }
    }, [settingsData.OzHr]);

    // Debounced effect to send ozoneHours
    useEffect(() => {
        if (lastUserOzHr !== null && ozoneHours === lastUserOzHr) {
            const timer = setTimeout(() => {
                command.updateSettings({ setOzHr: lastUserOzHr });
            }, inputDebounce);
            return () => clearTimeout(timer);
        }
    }, [lastUserOzHr, ozoneHours]);

    // Sync local state with backend for onzenHours
    useEffect(() => {
        if (settingsData.OnHr !== undefined) {
            const now = Date.now();
            const backendOnHr = settingsData.OnHr as number;
            if (
                lastUserOnHr === null &&
                now - lastUpdateOnHr > settingsDebounce &&
                onzenHours !== backendOnHr
            ) {
                setOnzenHours(backendOnHr);
            }
            if (lastUserOnHr !== null && backendOnHr === lastUserOnHr) {
                setLastUserOnHr(null);
            }
        }
    }, [settingsData.OnHr]);

    // Debounced effect to send onzenHours
    useEffect(() => {
        if (lastUserOnHr !== null && onzenHours === lastUserOnHr) {
            const timer = setTimeout(() => {
                command.updateSettings({ setOnHr: lastUserOnHr });
            }, inputDebounce);
            return () => clearTimeout(timer);
        }
    }, [lastUserOnHr, onzenHours]);

    // Handlers for user input (match SpaControlPage)
    const handleOzCycleChange = (value: number) => {
        setOzoneCycle(value);
        setLastUserOzCy(value);
        setLastUpdateOzCy(Date.now());
    };
    const handleOzHourChange = (value: number) => {
        setOzoneHours(value);
        setLastUserOzHr(value);
        setLastUpdateOzHr(Date.now());
    };
    const handleOnHourChange = (value: number) => {
        setOnzenHours(value);
        setLastUserOnHr(value);
        setLastUpdateOnHr(Date.now());
    };

    const isOffline = !isConnected && !isConnecting;
    const controlsDisabled = !isConnected;
    // Artwork turns red while running; plain when idle or offline.
    const iconState = (running: boolean) => isConnected && running ? 'selected' : 'default';
    const hoursUnit = (hours: number) => `${hours === 1 ? 'Hour' : 'Hours'} / Day`;
    const statusChip = (running: boolean) => isConnected && (
        <span className={`chip sanitation-status ${running ? 'active' : ''}`}>{running ? 'Active' : 'Idle'}</span>
    );
    const ozoneRunning = Boolean(liveData[SpaLiveLabels.Oz]);
    const onzenRunning = Boolean(liveData[SpaLiveLabels.On]);

    return (
        <div className="page sanitation-page">
            <Header/>
            {isConnecting ?
            <div className="sanitation-loading"><span className="spinner" role="progressbar" aria-label="Loading"/></div>
            :
            <main className={`sanitation-main ${isOffline ? 'offline' : ''}`}>
                {isOffline && <div className="page-notice">Spa is offline</div>}
                <StepperRows>
                    {settingsData[SpaSettingsLabels.cfgOzP1] &&
                        <StepperRow
                            icon={<div className={`status-avatar sanitation-icon-img ${iconState(ozoneRunning)}`} id="Ozone"></div>}
                            badge={statusChip(ozoneRunning)}
                            summary={subtitleMapper[ozoneCycle]}
                            mode
                            label="Ozone Peak I"
                            value={valueMapper[ozoneCycle]}
                            onDecrement={() => handleOzCycleChange(ozoneCycle - 1)}
                            onIncrement={() => handleOzCycleChange(ozoneCycle + 1)}
                            decrementDisabled={ozoneCycle <= (constData[SpaConstLabels.setOzCymin] ?? 0) || controlsDisabled}
                            incrementDisabled={ozoneCycle >= (constData[SpaConstLabels.setOzCymax] ?? 3) || controlsDisabled}
                        />
                    }
                    {settingsData[SpaSettingsLabels.cfgOzP2] &&
                        <StepperRow
                            icon={<div className={`status-avatar sanitation-icon-img ${iconState(ozoneRunning)}`} id="Ozone"></div>}
                            badge={statusChip(ozoneRunning)}
                            summary={`Peak II Ozone will operate for a total of ${ozoneHours} ${ozoneHours === 1 ? 'hour' : 'hours'} per day.`}
                            label="Ozone Peak II"
                            value={ozoneHours}
                            unit={hoursUnit(ozoneHours)}
                            onDecrement={() => handleOzHourChange(ozoneHours - 1)}
                            onIncrement={() => handleOzHourChange(ozoneHours + 1)}
                            decrementDisabled={ozoneHours <= (constData[SpaConstLabels.setOzHrmin] ?? 0) || controlsDisabled}
                            incrementDisabled={ozoneHours >= (constData[SpaConstLabels.setOzHrmax] ?? 24) || controlsDisabled}
                        />
                    }
                    {settingsData[SpaSettingsLabels.cfgOn] &&
                        <StepperRow
                            icon={<div className={`status-avatar sanitation-icon-img ${iconState(onzenRunning)}`} id="Onzen"></div>}
                            badge={statusChip(onzenRunning)}
                            summary={`Onzen will operate for a total of ${onzenHours} ${onzenHours === 1 ? 'hour' : 'hours'} per day.`}
                            label="Onzen"
                            value={onzenHours}
                            unit={hoursUnit(onzenHours)}
                            onDecrement={() => handleOnHourChange(onzenHours - 1)}
                            onIncrement={() => handleOnHourChange(onzenHours + 1)}
                            decrementDisabled={onzenHours <= (constData[SpaConstLabels.setOnHrmin] ?? 0) || controlsDisabled}
                            incrementDisabled={onzenHours >= (constData[SpaConstLabels.setOnHrmax] ?? 24) || controlsDisabled}
                        />
                    }
                </StepperRows>
            </main>
            }
        </div>
    );
};

export default Sanitation;
